/**
 * Centralized color constants
 *
 * This file contains all color values used across the application
 * to maintain consistency and make styling configurable.
 */

// Border colors
export const BORDER_COLORS = {
  DEFAULT: '#E3E8EF',
  LIGHT: '#E0E0E0',
  LIGHT_GRAY: '#EAECF0',
  VERY_LIGHT: '#EEEEEE',
  LIGHT_GRAY_ALT: '#E9E9ED',
  DARK: '#C62828'
} as const;

// Background colors
export const BACKGROUND_COLORS = {
  LIGHT_GRAY: '#FAFAFA',
  WHITE: '#FFFFFF',
  PAPER: '#FFFFFF',
  SELECTED: '#e3f2fd',
  HOVER: '#f5f5f5',
  SELECTED_HOVER: '#bbdefb',
  CALENDAR_HEADER: '#f8f9fa',
  /** Month view: days that belong to previous/next month (muted background). */
  CALENDAR_OTHER_MONTH: '#F0F1F3',
  USER_TIMEZONE: '#F9FAFC',
  INDIGO_LIGHT: '#E8EAF6',
  GREY_200: '#EEEEEE'
} as const;

// Text colors
export const TEXT_COLORS = {
  TEXT_PRIMARY: 'rgba(0, 0, 0, 0.87)',
  TEXT_SECONDARY: 'rgba(0, 0, 0, 0.60)',
  GREY_600: '#757575',
  COOLGRAY_90: '#21272A',
  MUTED: '#666',
  NEAR_BLACK: '#1a1a1a',
  MATERIAL_ICON: '#1C1B1F',
  HIGHLIGHT: '#2C60AE',
  WHITE: '#FFFFFF',
  BLACK: '#000000'
} as const;

// Status colors
export const STATUS_COLORS = {
  ERROR_MAIN: '#f44336',
  ERROR_THEME_MAIN: '#D32F2F',
  ERROR_DARK: '#c62828',
  WARNING_MAIN: '#EF6C00',
  WARNING_DARK: '#ed6c02',
  ORANGE_MAIN: '#ff9800',
  SUCCESS_MAIN: '#2e7d32',
  SUCCESS_DARK: '#1b5e20',
  SUCCESS_LIGHT_BG: '#e8f5e9',
  INFO_MAIN: '#0288D1',
  INFO_DARK: '#1976d2',
  INFO_DARKER: '#01579b',
  INFO_BLUE_900: '#0d47a1',
  INDIGO: '#3F51B5',
  PURPLE_DARK: '#7b1fa2',
  PURPLE_DARKER: '#512da8',
  CYAN_DARK: '#00838f',
  TEAL_DARK: '#00695c',
  // Status chip backgrounds
  SUCCESS_BACKGROUND: '#edf7ed',
  SUCCESS_BORDER: '#c8e6c9',
  INFO_BACKGROUND: '#e3f2fd',
  INFO_BORDER: '#bbdefb',
  WARNING_BACKGROUND: '#fff3e0',
  WARNING_COLOR: '#f57c00',
  WARNING_BORDER: '#ffcc02',
  WARNING_ICON: '#fb8c00',
  ERROR_BACKGROUND: '#fdeded',
  ERROR_BORDER: '#ffcdd2',
  PURPLE_LIGHT_BACKGROUND: '#e8eaf6',
  PURPLE_BACKGROUND: '#ede7f6',
  PURPLE_COLOR: '#5e35b1',
  PURPLE_BORDER: '#ce93d8',
  PURPLE_LIGHT: '#f3e5f5',
  BLUE_BACKGROUND: '#e1f5fe',
  BLUE_COLOR: '#0277bd',
  BLUE_BORDER: '#81d4fa',
  CYAN_BACKGROUND: '#e0f7fa',
  CYAN_COLOR: '#00838f',
  TEAL_BACKGROUND: '#e0f2f1',
  TEAL_COLOR: '#00796b',
  DEFAULT_BACKGROUND: '#f5f5f5',
  DEFAULT_COLOR: '#757575',
  DEFAULT_BORDER: '#e0e0e0',
  DEFAULT_TEXT: '#424242',
  AMBER_50: '#fff8e1',
  AMBER_800: '#ff8f00',
  LIGHTGREEN_50: '#f1f8e9',
  LIGHTGREEN_800: '#558b2f',
  INDIGO_700: '#303f9f',
  RED_50: '#feebee',
  BANNED_BACKGROUND: '#FDEDED',
  BLUE_GRAY_500: '#607D8B',
  BROWN_500: '#795548',
  BLUE_500: '#2196F3'
} as const;

// Interactive colors
export const INTERACTIVE_COLORS = {
  DRAG_ACTIVE: '#2196f3',
  DRAG_INACTIVE: '#eeeeee'
} as const;

export const RGBA_COLORS = {
  BLACK_02: 'rgba(0, 0, 0, 0.02)',
  BLACK_04: 'rgba(0, 0, 0, 0.04)',
  BLACK_05: 'rgba(0, 0, 0, 0.05)',
  BLACK_08: 'rgba(0, 0, 0, 0.08)',
  BLACK_10: 'rgba(0, 0, 0, 0.10)',
  BLACK_12: 'rgba(0, 0, 0, 0.12)',
  BLACK_15: 'rgba(0, 0, 0, 0.15)',
  BLACK_25: 'rgba(0, 0, 0, 0.25)',
  BLACK_26: 'rgba(0, 0, 0, 0.26)',
  BLACK_38: 'rgba(0, 0, 0, 0.38)',
  BLACK_54: 'rgba(0, 0, 0, 0.54)',
  BLACK_60: 'rgba(0, 0, 0, 0.6)',
  BLACK_80: 'rgba(0, 0, 0, 0.8)',
  WHITE_00: 'rgba(255, 255, 255, 0)',
  WHITE_60: 'rgba(255, 255, 255, 0.6)',
  WHITE_70: 'rgba(255, 255, 255, 0.7)',
  WHITE_80: 'rgba(255, 255, 255, 0.8)',
  WHITE_90: 'rgba(255, 255, 255, 0.9)',
  WHITE_95: 'rgba(255, 255, 255, 0.95)',
  PRIMARY_04: 'rgba(35, 77, 140, 0.04)',
  PRIMARY_08: 'rgba(35, 77, 140, 0.08)',
  PRIMARY_12: 'rgba(35, 77, 140, 0.12)',
  PRIMARY_50: 'rgba(35, 77, 140, 0.50)',
  BLUE_08: 'rgba(25, 118, 210, 0.08)',
  BLUE_12: 'rgba(25, 118, 210, 0.12)',
  GREEN_08: 'rgba(76, 175, 80, 0.08)',
  ORANGE_08: 'rgba(255, 152, 0, 0.08)',
  RED_08: 'rgba(244, 67, 54, 0.08)',
  PURPLE_08: 'rgba(156, 39, 176, 0.08)',
  CYAN_08: 'rgba(0, 188, 212, 0.08)',
  TEAL_08: 'rgba(0, 150, 136, 0.08)',
  ERROR_08: 'rgba(211, 47, 47, 0.08)',
  SUCCESS_08: 'rgba(46, 125, 50, 0.08)',
  SECONDARY_12: 'rgba(77, 88, 255, 0.12)',
  SECONDARY_DARK_12: 'rgba(40, 53, 147, 0.12)',
  GRAY_08: 'rgba(177, 177, 177, 0.08)',
  GRAY_90: 'rgba(97, 97, 97, 0.9)',
  GRAY_12: 'rgba(0, 0, 0, 0.12)',
  GRAY_14: 'rgba(0, 0, 0, 0.14)',
  GRAY_20: 'rgba(0, 0, 0, 0.2)'
} as const;

// DataTable specific colors
export const DATATABLE_COLORS = {
  BORDER_LIGHT: 'rgba(224, 224, 224, 1)',
  BORDER_DARK: 'rgba(189, 189, 189, 1)',
  DENSITY_MENU_ACTIVE: RGBA_COLORS.BLUE_08,
  TOOLTIP_BACKGROUND: RGBA_COLORS.GRAY_90
} as const;

export const TOOLTIP_COLORS = {
  DARK_BACKGROUND: '#343A3F',
  MASK_COLOR: '#D9D9D9'
} as const;

// Notes card color palette
export const NOTES_COLORS = {
  BACKGROUND: '#FFF8E1',
  ICON: '#FFB300'
} as const;

// OOS (Out of Service) dialog color palette
export const OOS_COLORS = {
  HEADER_BACKGROUND: '#fff3cd',
  HEADER_BORDER: '#ffeaa7',
  AVATAR_BACKGROUND: '#f39c12',
  TEXT_DARK: '#856404',
  ICON_WARNING: '#f39c12',
  DETAIL_BACKGROUND: '#f8f9fa',
  DETAIL_BORDER: '#e9ecef',
  CONFIRM_BUTTON: '#f39c12',
  CONFIRM_BUTTON_HOVER: '#e67e22',
  DISABLED_BUTTON: '#bdc3c7',
  CANCEL_HOVER_BG: 'rgba(133, 100, 4, 0.1)',
  ERROR_HOVER_BG: 'rgba(211, 47, 47, 0.04)',
  SHADOW: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.20)'
} as const;

// Map component color palette
export const MAP_COLORS = {
  CLUSTER_GRADIENT_START: '#1e3a5f',
  CLUSTER_GRADIENT_END: '#2c5aa0',
  INNER_GRADIENT_START: '#2c3e50',
  INNER_GRADIENT_MID: '#34495e',
  CLUSTER_SHADOW: '0 6px 20px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)',
  CLUSTER_SHADOW_HOVER: '0 8px 25px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3)',
  DETAILS_SHADOW: '0 4px 20px rgba(0, 0, 0, 0.15)',
  DETAILS_BORDER: 'rgba(0, 0, 0, 0.1)',
  FEATURE_HIGHLIGHT: 'rgba(52, 152, 219, 0.1)'
} as const;

// Card shadow and styling
export const CARD_SHADOWS = {
  DEFAULT: `0 6px 10px 0 ${RGBA_COLORS.GRAY_08}, 0 1px 3px 0 ${RGBA_COLORS.BLACK_02}`
} as const;

// Category colors for requirements and other category-based features
export const CATEGORY_COLORS = {
  SAFETY: '#FF5722',
  LEGAL: '#3F51B5',
  ENVIRONMENTAL: '#4CAF50',
  INSURANCE: STATUS_COLORS.ORANGE_MAIN,
  DEFAULT: '#9E9E9E'
} as const;

// RaptorJob specific colors
export const RAPTORJOB_COLORS = {
  SECONDARY_DARK: '#283593',
  SUCCESS_GREEN: '#43A047',
  SUCCESS_BACKGROUND_ALT: '#E8F5E9',
  ORANGE_600: '#FF6F00',
  CARD_ICON_BLUE: '#1B3A6A',
  CARD_ICON_BLUE_ALPHA: 'rgba(35, 77, 140, 0.12)',
  COMMENTS_BACKGROUND: '#FFF8E1',
  COMMENTS_ICON: '#FFB300'
} as const;

// Timeline specific colors
export const TIMELINE_COLORS = {
  BLUE_DARK: '#01579B',
  /** Cleared time-diff bar when job finished later than estimated (overtime) */
  CLEARED_DIFF_OVERTIME: '#c62828',
  /** Cleared time-diff bar when job finished earlier than estimated */
  CLEARED_DIFF_EARLY: '#43A047'
} as const;

// Calendar event card (day/week/month event chips)
export const CALENDAR_EVENT_COLORS = {
  BACKGROUND: '#E0F2FE',
  ACCENT_BAR: '#1976D2',
  TIME: '#667085',
  TITLE: '#0A60B3'
} as const;

export const ALPHA_COLORS = {
  PRIMARY_08: '#234D8C14',
  PRIMARY_30: '#234D8C4D',
  PRIMARY_50: '#234D8C80'
} as const;

// CertPerm module colors (Permits & Certifications)
export const CERTPERM_COLORS = {
  PRIMARY_ALPHA: '#234D8C1F',
  PRIMARY_DARK: '#1a3d6b',
  PRIMARY_LIGHT_ALPHA: '#4D58FF1F',
  SUCCESS_BACKGROUND: '#EDF7ED',
  WARNING: '#663C00',
  WARNING_BACKGROUND: '#FFF4E5',
  ERROR_BACKGROUND: '#FDEDED',
  BORDER_LIGHT: BORDER_COLORS.LIGHT,
  BORDER_DARK: DATATABLE_COLORS.BORDER_DARK,
  TEXT_DISABLED: RGBA_COLORS.BLACK_60,
  BACKGROUND_NEUTRAL: '#f5f5f5',
  BACKGROUND_WHITE: BACKGROUND_COLORS.WHITE,
  BACKGROUND_OVERLAY: RGBA_COLORS.PRIMARY_04,
  UPLOAD_ACTIVE: RGBA_COLORS.BLUE_08,
  UPLOAD_HOVER: RGBA_COLORS.BLUE_12,
  UPLOAD_BORDER_COLOR: '#234d8c',
  SHADOW_LIGHT: `0 8px 32px ${RGBA_COLORS.GRAY_12}`,
  SHADOW_BUTTON: `0px 1px 5px 0px ${RGBA_COLORS.GRAY_12}, 0px 2px 2px 0px ${RGBA_COLORS.GRAY_14}, 0px 3px 1px -2px ${RGBA_COLORS.GRAY_20}`,
  // Theme palette color values for non-React files
  // React components should use 'primary.main' in sx or theme.palette.primary.main
  PRIMARY: '#234D8C',
  PRIMARY_LIGHT: '#42A5F5',
  SUCCESS: '#2E7D32',
  ERROR: '#D32F2F'
} as const;

// MUI default theme palette colors (standard Material Design defaults used in test mocks)
export const MUI_PALETTE = {
  PRIMARY_LIGHT: '#42A5F5',
  SECONDARY_MAIN: '#9C27B0',
  SECONDARY_LIGHT: '#BA68C8',
  SECONDARY_DARK: '#7B1FA2',
  ERROR_LIGHT: '#EF5350',
  INFO_LIGHT: '#03A9F4',
  SUCCESS_LIGHT: '#4CAF50'
} as const;

// Standard grey palette (Material Design scale)
export const GREY_PALETTE = {
  GREY_50: '#FAFAFA',
  GREY_100: '#F5F5F5',
  GREY_200: '#EEEEEE',
  GREY_300: '#E0E0E0',
  GREY_400: '#BDBDBD',
  GREY_500: '#9E9E9E',
  GREY_600: '#757575',
  GREY_700: '#616161',
  GREY_800: '#424242',
  GREY_900: '#212121'
} as const;

// Job Site module colors
export const JOB_SITE_COLORS = {
  background: '#FAFAFA',
  header: {
    background: '#FFFFFF',
    border: '#E2E8F0',
    title: '#1E293B',
    subtitle: '#64748B',
    status: {
      active: '#10B981',
      inactive: '#6B7280'
    }
  },
  tabs: {
    active: '#3B82F6',
    inactive: '#64748B',
    border: '#E2E8F0'
  },
  cards: {
    background: '#FFFFFF',
    border: '#E2E8F0',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    hover: '#F8FAFC'
  },
  metrics: {
    primary: '#3B82F6',
    secondary: '#10B981',
    warning: '#F59E0B',
    warningLight: '#FFFBEB',
    danger: '#EF4444',
    error: '#EF4444',
    info: '#E5F6FD'
  },
  status: {
    active: '#10B981',
    activeText: '#065F46',
    activeBackground: '#EDF7ED',
    activeColor: '#2E7D32',
    pending: '#F59E0B',
    pendingText: '#92400E',
    expired: '#EF4444',
    expiredText: '#991B1B',
    completed: '#3B82F6',
    completedText: '#1E40AF',
    completedBackground: '#E5F6FD',
    completedColor: '#01579B',
    paused: '#EF6C00',
    pausedBackground: '#FFF4E5',
    pausedColor: '#EF6C00',
    expiringSoon: '#FFEDD5',
    expiringSoonText: '#F97316'
  },
  contacts: {
    title: '#21272A',
    primaryLabel: {
      background: '#FFFDE7',
      text: '#F57F17'
    },
    star: {
      primary: '#F57F17',
      secondary: '#FFFDE7'
    }
  },
  chips: {
    purple: {
      purple50: '#EDE7F6',
      purple600: '#5E35B1'
    },
    cyan: {
      cyan50: '#E0F7FA',
      cyan800: '#00838F'
    },
    teal: {
      teal50: '#E0F2F1',
      teal700: '#00796B'
    }
  }
} as const;

export const BRAND_COLORS = {
  ORANGE: '#FF5403'
} as const;

export const SIDEBAR_COLORS = {
  BACKGROUND: '#262839',
  TOGGLE_HOVER: BRAND_COLORS.ORANGE,
  AVATAR_BACKGROUND: '#60a5fa'
} as const;

// Semantic and component colors (legacy - prefer specific color groups above)
export const COLORS = {
  semantic: {
    warning: {
      light: '#F59E0B'
    },
    yellow: {
      '700': '#FBC02D'
    },
    grey: {
      '600': '#4B5565'
    },
    action: {
      blue: '#1E40AF'
    },
    dialog: {
      titleText: '#3F484A'
    }
  },
  components: {
    alert: {
      warning: {
        background: '#FFF4E5',
        color: '#663C00'
      }
    }
  }
} as const;

// Dispatch grid — text colors for job round tracking (from legacy spec, not user-configurable)
export const JOB_ROUND_TEXT_COLORS = {
  HOLD_MUD: '#FF0000',
  SECOND_ROUND: '#0000FF',
  THIRD_ROUND: '#FFA500',
  FOURTH_ROUND: '#B8860B',
  FIFTH_ROUND: '#008000',
  DEFAULT: '#000000'
} as const;
